import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { RatingDoc } from "@/dao/firestore/FeedbackSummaryRepository";
import { FeedbackResultDoc } from "@/dao/firestore/FeedbackResultsRepository";
import { useTranslation } from "react-i18next";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Cell, Tooltip as RechartsTooltip } from "recharts";
import { TrendingUp, TrendingDown, Star, BarChart3, ChevronDown, ChevronUp } from "lucide-react";

interface RatingsModalProps {
  open: boolean;
  onClose: () => void;
  ratings: RatingDoc[];
  personalFeedbackResults: FeedbackResultDoc[];
}

interface RadarData {
  subject: string;
  avg: number;
  min: number;
  max: number;
  comment: string;
}


const getRatingColor = (rating: number) => {
  if (rating >= 8) return "text-green-600";
  if (rating >= 6) return "text-yellow-600";
  return "text-red-600";
};

export function RatingsModal({ open, onClose, ratings, personalFeedbackResults }: RatingsModalProps) {
  const { t } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  // Transform ratings data for radar chart (values already on 10-point scale)
  const radarData: RadarData[] = ratings.map(rating => ({
    subject: t(`screeningRoom.ratings.categories.${rating.type}`, rating.type),
    avg: rating.avg, // Use original values (already 0-10 scale)
    min: rating.min,
    max: rating.max,
    comment: rating.comment
  }));

  // Calculate overall statistics (values already on 10-point scale)
  const overallAvg = ratings.length > 0 ? ratings.reduce((sum, r) => sum + r.avg, 0) / ratings.length : 0;
  const highestRated = ratings.reduce((prev, current) => (prev.avg > current.avg) ? prev : current, ratings[0]);
  const lowestRated = ratings.reduce((prev, current) => (prev.avg < current.avg) ? prev : current, ratings[0]);

  // Extract individual ratings from personal feedback results
  const individualRatings = personalFeedbackResults.flatMap((result, index) => {
    const ratings = [];
    if (result.visual) ratings.push({ type: 'visual', rating: result.visual.rating, comment: result.visual.comment, personaName: result.personaName || `${t('common.user')} ${index + 1}` });
    if (result.content) ratings.push({ type: 'content', rating: result.content.rating, comment: result.content.comment, personaName: result.personaName || `${t('common.user')} ${index + 1}` });
    if (result.script) ratings.push({ type: 'script', rating: result.script.rating, comment: result.script.comment, personaName: result.personaName || `${t('common.user')} ${index + 1}` });
    if (result.other) ratings.push({ type: 'other', rating: result.other.rating, comment: result.other.comment, personaName: result.personaName || `${t('common.user')} ${index + 1}` });
    if (result.sound_design) ratings.push({ type: 'sound_design', rating: result.sound_design.rating, comment: result.sound_design.comment, personaName: result.personaName || `${t('common.user')} ${index + 1}` });
    if (result.pacing) ratings.push({ type: 'pacing', rating: result.pacing.rating, comment: result.pacing.comment, personaName: result.personaName || `${t('common.user')} ${index + 1}` });
    if (result.performance) ratings.push({ type: 'performance', rating: result.performance.rating, comment: result.performance.comment, personaName: result.personaName || `${t('common.user')} ${index + 1}` });
    if (result.target_focus) ratings.push({ type: 'target_focus', rating: result.target_focus.rating, comment: result.target_focus.comment, personaName: result.personaName || `${t('common.user')} ${index + 1}` });
    return ratings;
  });

  // Group individual ratings by type
  const ratingsByType = individualRatings.reduce((acc, rating) => {
    if (!acc[rating.type]) acc[rating.type] = [];
    acc[rating.type].push(rating);
    return acc;
  }, {} as Record<string, any[]>);

  const toggleExpanded = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg max-w-xs">
          <p className="font-semibold text-popover-foreground">{data.subject}</p>
          <p className="text-sm text-muted-foreground">
            {t('screeningRoom.ratings.score')}: {data.avg.toFixed(1)}/10
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('common.range')}: {data.min.toFixed(1)} - {data.max.toFixed(1)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5" />
            {t('screeningRoom.ratings.title')}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[70vh]">
          <div className="space-y-6 p-1">
            
            {/* Overall Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{t('screeningRoom.ratings.overallAverage')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{overallAvg.toFixed(1)}</div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <BarChart3 className="h-4 w-4 mr-1" />
                    {t('common.outOf')} 10.0
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{t('screeningRoom.ratings.highestRated')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-emerald-500" />
                    <div>
                      <div className="font-semibold">{t(`screeningRoom.ratings.categories.${highestRated?.type}`, highestRated?.type)}</div>
                      <div className="text-sm text-muted-foreground">{highestRated?.avg.toFixed(1)}/10</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{t('screeningRoom.ratings.lowestRated')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <TrendingDown className="h-4 w-4 text-destructive" />
                    <div>
                      <div className="font-semibold">{t(`screeningRoom.ratings.categories.${lowestRated?.type}`, lowestRated?.type)}</div>
                      <div className="text-sm text-muted-foreground">{lowestRated?.avg.toFixed(1)}/10</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Radar Chart */}
            {radarData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('screeningRoom.ratings.radarChart')}</CardTitle>
                  <CardDescription>{t('screeningRoom.ratings.radarDescription')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="subject" className="text-xs" />
                        <PolarRadiusAxis 
                          angle={90} 
                          domain={[0, 10]} 
                          tickCount={6}
                          className="text-xs"
                        />
                        <Radar
                          name="Average"
                          dataKey="avg"
                          stroke="hsl(var(--chart-1))"
                          fill="hsl(var(--chart-1))"
                          fillOpacity={0.25}
                          strokeWidth={3}
                        />
                        <Radar
                          name="Maximum"
                          dataKey="max"
                          stroke="hsl(var(--chart-2))"
                          fill="transparent"
                          strokeWidth={1.5}
                          strokeDasharray="8 4"
                          strokeOpacity={0.7}
                        />
                        <Radar
                          name="Minimum"
                          dataKey="min"
                          stroke="hsl(var(--chart-3))"
                          fill="transparent"
                          strokeWidth={1.5}
                          strokeDasharray="8 4"
                          strokeOpacity={0.7}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Detailed Analysis */}
            <Card>
              <CardHeader>
                <CardTitle>{t('screeningRoom.ratings.detailedAnalysis')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {ratings.map((rating) => {
                    const individualRatingsForType = ratingsByType[rating.type] || [];
                    const individualScores = individualRatingsForType.map(r => r.rating);
                    const isExpanded = expandedCategories[rating.type];
                    
                    return (
                      <div key={rating.type} className="border border-border rounded-lg p-4 bg-card">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-3">
                            <h4 className="font-semibold text-card-foreground">{t(`screeningRoom.ratings.categories.${rating.type}`, rating.type)}</h4>
                            <Badge variant={rating.avg > 7.5 ? "default" : rating.avg > 5.5 ? "secondary" : "destructive"}>
                              {rating.avg.toFixed(1)}/10
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {t('common.range')}: {rating.min.toFixed(1)} - {rating.max.toFixed(1)}
                          </div>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-3">{rating.comment}</p>
                        
                        {individualRatingsForType.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium">
                                {t('screeningRoom.ratings.individualRatings')} ({individualRatingsForType.length})
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleExpanded(rating.type)}
                                className="h-8 w-8 p-0"
                              >
                                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </Button>
                            </div>
                            
                             {isExpanded && (
                               <div className="space-y-3 pt-2 border-t border-border">
                                 {individualRatingsForType.map((ratingData, index) => {
                                   // Debug logging to understand data structure
                                   console.log('ratingData:', ratingData);
                                   console.log('ratingData.rating:', ratingData.rating, 'type:', typeof ratingData.rating);
                                   
                                   const safeRating = typeof Number(ratingData.rating) === 'number' ? Number(ratingData.rating) : 0;
                                   
                                   return (
                                     <div key={index} className="space-y-2">
                                       <div className="flex justify-between items-center">
                                         <span className="text-sm font-medium text-card-foreground">
                                           {ratingData.personaName} <span className="text-xs text-muted-foreground">{t('studio.critics.inTheStyleOf')}</span>
                                         </span>
                                         <div className={`text-xs ${getRatingColor(ratingData.rating || 0)}`} >
                                           {safeRating.toFixed(1)}/10
                                         </div>
                                       </div>
                                       {}
                                       <Progress value={Number(ratingData.rating) * 10} className="mb-0 h-1" />
                                       {ratingData.comment && (
                                         <p className="text-xs text-muted-foreground italic">
                                           "{ratingData.comment}"
                                         </p>
                                       )}
                                     </div>
                                   );
                                 })}
                               </div>
                             )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}